import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Activity, RefreshCw, CheckCircle, AlertTriangle, XCircle, 
  Database, Server, Clock, Zap, Shield, Users, FileText,
  DollarSign, Calendar, Briefcase, Package, LogOut, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const moduleIcons = {
  Core: Shield,
  HR: Users,
  Compliance: FileText,
  Payroll: DollarSign,
  Attendance: Calendar,
  Leaves: Calendar,
  Recruitment: Briefcase,
  Accounting: DollarSign,
  Receivables: DollarSign,
  Exit: LogOut,
  Assets: Package,
  Documents: FileText
};

const statusColors = {
  healthy: 'bg-emerald-500',
  warning: 'bg-amber-500',
  critical: 'bg-rose-500',
  unhealthy: 'bg-rose-500',
  info: 'bg-blue-500',
  unknown: 'bg-slate-400',
  loading: 'bg-slate-300'
};

const statusBgColors = {
  healthy: 'bg-emerald-50 border-emerald-200',
  warning: 'bg-amber-50 border-amber-200',
  critical: 'bg-rose-50 border-rose-200',
  unhealthy: 'bg-rose-50 border-rose-200',
  info: 'bg-blue-50 border-blue-200',
  unknown: 'bg-slate-50 border-slate-200',
  loading: 'bg-slate-50 border-slate-200'
};

const HealthMonitor = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [endpointResults, setEndpointResults] = useState({});
  const [lastChecked, setLastChecked] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchHealthStatus = useCallback(async () => {
    try {
      const response = await api.get('/health/check-all');
      setHealthData(response.data);
      setLastChecked(new Date());
    } catch (error) {
      console.error('Failed to fetch health status:', error);
      toast.error('Failed to fetch health status');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkEndpoint = async (endpoint) => {
    const startTime = performance.now();
    try {
      const response = await api({
        method: endpoint.method,
        url: endpoint.endpoint,
        timeout: 10000
      });
      const responseTime = Math.round(performance.now() - startTime);
      
      return {
        ...endpoint,
        status: 'healthy',
        response_time_ms: responseTime,
        status_code: response.status,
        checked_at: new Date().toISOString()
      };
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      return {
        ...endpoint,
        status: error.response?.status === 404 ? 'warning' : 'unhealthy',
        response_time_ms: responseTime,
        status_code: error.response?.status || 0,
        error: error.message,
        checked_at: new Date().toISOString()
      };
    }
  };

  const runFullCheck = async () => {
    if (!healthData?.endpoints_to_test) return;
    
    setChecking(true);
    const results = {};
    
    for (const endpoint of healthData.endpoints_to_test) {
      const result = await checkEndpoint(endpoint);
      results[endpoint.endpoint] = result;
      setEndpointResults(prev => ({ ...prev, [endpoint.endpoint]: result }));
    }
    
    setChecking(false);
    setLastChecked(new Date());
    
    const unhealthy = Object.values(results).filter(r => r.status === 'unhealthy').length;
    const warnings = Object.values(results).filter(r => r.status === 'warning').length;
    
    if (unhealthy > 0) {
      toast.error(`${unhealthy} endpoint(s) are unhealthy!`);
    } else if (warnings > 0) {
      toast.warning(`${warnings} endpoint(s) have warnings`);
    } else {
      toast.success('All endpoints are healthy!');
    }
  };

  useEffect(() => {
    fetchHealthStatus();
  }, [fetchHealthStatus]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchHealthStatus();
        if (healthData?.endpoints_to_test) {
          runFullCheck();
        }
      }, 60000); // Refresh every 60 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealthStatus, healthData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="text-emerald-500" size={18} />;
      case 'warning':
        return <AlertTriangle className="text-amber-500" size={18} />;
      case 'critical':
      case 'unhealthy':
        return <XCircle className="text-rose-500" size={18} />;
      case 'info':
        return <Info className="text-blue-500" size={18} />;
      default:
        return <Clock className="text-slate-400" size={18} />;
    }
  };

  const getOverallStatusBadge = (status) => {
    const colors = {
      healthy: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      warning: 'bg-amber-100 text-amber-800 border-amber-300',
      critical: 'bg-rose-100 text-rose-800 border-rose-300',
      degraded: 'bg-amber-100 text-amber-800 border-amber-300'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${colors[status] || colors.warning}`}>
        {status?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  // Group endpoints by module
  const endpointsByModule = {};
  if (healthData?.endpoints_to_test) {
    for (const endpoint of healthData.endpoints_to_test) {
      const module = endpoint.module;
      if (!endpointsByModule[module]) {
        endpointsByModule[module] = [];
      }
      endpointsByModule[module].push({
        ...endpoint,
        result: endpointResults[endpoint.endpoint]
      });
    }
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="health-monitor-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="text-indigo-600" />
              API Health Monitor
            </h1>
            <p className="text-slate-600 mt-1">
              Monitor all critical API endpoints and system health
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastChecked && (
              <span className="text-sm text-slate-500">
                Last checked: {lastChecked.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'bg-indigo-50 border-indigo-300' : ''}
            >
              <RefreshCw size={16} className={`mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </Button>
            <Button
              onClick={runFullCheck}
              disabled={checking}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="run-health-check-btn"
            >
              {checking ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <Zap size={16} className="mr-2" />
                  Run Full Check
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Overall Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">System Status</p>
                  <div className="mt-2">
                    {getOverallStatusBadge(healthData?.overall_status)}
                  </div>
                </div>
                <Server className="h-12 w-12 text-indigo-200" />
              </div>
            </CardContent>
          </Card>

          <Card className={`border ${statusBgColors[healthData?.database?.status || 'unknown']}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Database</p>
                  <p className="text-2xl font-bold mt-1 capitalize">
                    {healthData?.database?.status || 'Unknown'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {healthData?.database?.response_time_ms}ms response
                  </p>
                </div>
                <Database className={`h-10 w-10 ${healthData?.database?.status === 'healthy' ? 'text-emerald-500' : 'text-rose-500'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Endpoints Monitored</p>
                  <p className="text-2xl font-bold mt-1">
                    {healthData?.endpoints_to_test?.length || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {healthData?.endpoints_to_test?.filter(e => e.critical).length || 0} critical
                  </p>
                </div>
                <Activity className="h-10 w-10 text-indigo-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Check Results</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="flex items-center gap-1 text-sm">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      {Object.values(endpointResults).filter(r => r?.status === 'healthy').length}
                    </span>
                    <span className="flex items-center gap-1 text-sm">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      {Object.values(endpointResults).filter(r => r?.status === 'warning').length}
                    </span>
                    <span className="flex items-center gap-1 text-sm">
                      <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                      {Object.values(endpointResults).filter(r => r?.status === 'unhealthy').length}
                    </span>
                  </div>
                </div>
                <CheckCircle className="h-10 w-10 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Health */}
        {healthData?.data_health && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="text-indigo-600" size={20} />
                Data Health Overview
              </CardTitle>
              <CardDescription>Collection counts and data integrity checks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {healthData.data_health.map((item, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${statusBgColors[item.status]}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">{item.name}</span>
                      {getStatusIcon(item.status)}
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {item.count >= 0 ? item.count.toLocaleString() : 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 truncate" title={item.message}>
                      {item.message}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Endpoints by Module */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(endpointsByModule).map(([module, endpoints]) => {
            const ModuleIcon = moduleIcons[module] || Server;
            const healthyCount = endpoints.filter(e => e.result?.status === 'healthy').length;
            const checkedCount = endpoints.filter(e => e.result).length;
            
            return (
              <Card key={module}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <ModuleIcon className="text-indigo-600" size={18} />
                      {module}
                    </span>
                    {checkedCount > 0 && (
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        healthyCount === checkedCount 
                          ? 'bg-emerald-100 text-emerald-700'
                          : healthyCount > 0 
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-rose-100 text-rose-700'
                      }`}>
                        {healthyCount}/{checkedCount} OK
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {endpoints.map((endpoint, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          endpoint.result 
                            ? statusBgColors[endpoint.result.status]
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {endpoint.result ? getStatusIcon(endpoint.result.status) : (
                            <Clock className="text-slate-400" size={18} />
                          )}
                          <div>
                            <p className="font-medium text-sm text-slate-800">
                              {endpoint.name}
                              {endpoint.critical && (
                                <span className="ml-2 text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
                                  CRITICAL
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500 font-mono">
                              {endpoint.method} {endpoint.endpoint}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {endpoint.result ? (
                            <>
                              <p className={`text-sm font-medium ${
                                endpoint.result.response_time_ms > 1000 
                                  ? 'text-amber-600' 
                                  : 'text-slate-700'
                              }`}>
                                {endpoint.result.response_time_ms}ms
                              </p>
                              <p className="text-xs text-slate-500">
                                {endpoint.result.status_code || 'N/A'}
                              </p>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400">Not checked</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Collection Counts */}
        {healthData?.collection_counts && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="text-indigo-600" size={20} />
                Database Collections
              </CardTitle>
              <CardDescription>Document counts per collection</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {Object.entries(healthData.collection_counts).map(([collection, count]) => (
                  <div 
                    key={collection}
                    className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-center"
                  >
                    <p className="text-xs text-slate-500 capitalize">
                      {collection.replace(/_/g, ' ')}
                    </p>
                    <p className="text-lg font-bold text-slate-800">
                      {count >= 0 ? count.toLocaleString() : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default HealthMonitor;
